import React from 'react';
import { hydrateRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { I18nextProvider } from 'react-i18next';
import { Provider } from 'react-redux';
import flagsmith from 'flagsmith';
import { FlagsmithProvider } from 'flagsmith/react';
import smoothScroll from 'smoothscroll-polyfill';

import { getUniversalEnv } from 'app/lib/get-universal-env';

const cdnStatic = getUniversalEnv('CDN_STATIC');
if (cdnStatic) {
  __webpack_public_path__ = `${cdnStatic}/static/`;
}


import { createHttpApiService } from 'app/api';
import { FetchContext } from 'app/api/useFetch/context';
import { LanguageStore } from 'app/lang';
import { saveBrowserTabIdListener } from 'app/lib/browserTabId';
import ErrorBoundary from 'app/widgets/ErrorBoundary';

import App from './App';
import initClientConfigs from './init-client-configs';
import { themeInstance } from './lib/localStorages';
import ConnectedRouter from './lib/router';
import { initializeWebSockets } from './lib/signalr';
import { Theme, ThemeContextProvider } from './lib/theme';
import { UserAgentProvider } from './lib/user-agent';
import UnknownError from './modules/UnknownError';
import { configureClientStore } from './redux/configureClientStore';
import sagas from './redux/sagas/client';
import Routes from './routes';

import './design-system/styles/typography.module.scss';
import './design-system/styles/index.scss';
import './styles/index.scss';

// ---

// TODO: Удалить в рамках SITE-1125 (класс темы будет сетится на SSRе)
const INITIAL_THEME = themeInstance.get() || Theme.LIGHT;

const html = document.querySelector('html');
html && html.classList.add(INITIAL_THEME);
//

const domNode = document.getElementById('app-root');

saveBrowserTabIdListener();
initClientConfigs(initClientApplication, initErrorPage);

async function initClientApplication() {
  await LanguageStore.createInstance(
    window.__langFromSSR,
    window.__data?.siteConfigs?.supportedLanguages,
  ).catch((error) => console.error(error));
  const flagsmithState = window.__fss;
  const store = configureClientStore(window.__data, {
    language: LanguageStore.getLang(),
    brandName: window.__env.BRAND_NAME,
    createHttpApiService,
    flagsmithState,
  });

  smoothScroll.polyfill();
  window.__data = undefined;
  // Remove after complete transition on ANTLR ->
  // window.__fss = undefined;
  // Remove after complete transition on ANTLR <-

  // We decided to use render in favor of hydrate
  // Because hydrate causes viewport problem on frontend
  // where it calculates one viewport on server
  // and sends html based on that viewport to client
  // so it causes UI bugs that can't fixed manually
  // So Mikhail said it's better to use render instead hydrate
  hydrateRoot(
    domNode,
    <ErrorBoundary>
      <HelmetProvider>
        <I18nextProvider i18n={LanguageStore.getInstance()}>
          <Provider store={store}>
            <ConnectedRouter>
              <FetchContext.Provider value={window.__useFetchState}>
                <FlagsmithProvider serverState={flagsmithState}
                                   options={{
                                     cacheFlags: true,
                                   }}
                                   flagsmith={flagsmith} >
                  <ThemeContextProvider initialTheme={INITIAL_THEME}>
                    <UserAgentProvider value={navigator.userAgent}>
                      <App createHttpApiService={createHttpApiService}>
                        <Routes />
                      </App>
                    </UserAgentProvider>
                  </ThemeContextProvider>
                </FlagsmithProvider>
              </FetchContext.Provider>
            </ConnectedRouter>
          </Provider>
        </I18nextProvider>
      </HelmetProvider>
    </ErrorBoundary>,
  );

  initializeWebSockets(store);
  // FIXME: cant work with old configuration
  // store.restore();
  store.runSagas(sagas);
}

function initErrorPage() {
  hydrateRoot(
    domNode,
    <ThemeContextProvider initialTheme={INITIAL_THEME}>
      <UnknownError />
    </ThemeContextProvider>,
  );
}
